import { Chip, IconButton, Paper, Stack, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { HallMaintenanceTask } from '../../domain/HallMaintenanceTask';
import { alpha } from '@mui/material/styles';

interface Props {
    tasks: HallMaintenanceTask[];
    canManage: boolean;
    onView: (task: HallMaintenanceTask) => void;
    onEdit: (task: HallMaintenanceTask) => void;
    onDelete: (task: HallMaintenanceTask) => void;
    onAssignments: (task: HallMaintenanceTask) => void;
}

const HallMaintenanceTasksMobileList = (props: Props) => {
    const { t } = useTranslation();
    const monthNames = moment.monthsShort();
    const currentMonth = moment().month() + 1;

    const formatFrequency = (task: HallMaintenanceTask) => {
        if (task.frequencyLabel) {
            return task.frequencyLabel;
        }
        const interval = task.frequencyInterval || 1;
        const unitLabel = t(task.frequencyUnit);
        return `${interval} ${unitLabel}`;
    };

    const formatMonths = (task: HallMaintenanceTask) => {
        if (!task.plannedMonths || task.plannedMonths.length === 0) {
            return <Typography variant="caption" color="text.secondary">{`${t("planned_months")}: ${t("no_data")}`}</Typography>;
        }

        const sortedMonths = [...task.plannedMonths].sort((a, b) => a - b);
        const maxVisible = 4;
        const visibleMonths = sortedMonths.slice(0, maxVisible);
        const extraCount = sortedMonths.length - visibleMonths.length;

        return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {visibleMonths.map(month => (
                    <Chip
                        key={`${task.id}-${month}`}
                        size="small"
                        label={monthNames[(month - 1 + 12) % 12]}
                        variant="outlined"
                        color={month === currentMonth ? 'success' : 'default'}
                        sx={{ fontWeight: month === currentMonth ? 600 : undefined }}
                    />
                ))}
                {extraCount > 0 && (
                    <Chip
                        size="small"
                        label={`+${extraCount}`}
                        color="primary"
                        variant="outlined"
                    />
                )}
            </Stack>
        );
    };

    const isPlannedForCurrentMonth = (task: HallMaintenanceTask) => {
        return task.plannedMonths?.includes(currentMonth);
    };

    return (
        <Stack spacing={1.5}>
            {props.tasks.map(task => (
                <Paper
                    key={task.id}
                    elevation={1}
                    sx={{
                        p: 1.5,
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'transform 120ms ease, box-shadow 120ms ease',
                        border: (theme) => isPlannedForCurrentMonth(task)
                            ? `1px solid ${alpha(theme.palette.success.main, 0.4)}`
                            : `1px solid ${theme.palette.divider}`,
                        backgroundColor: (theme) => isPlannedForCurrentMonth(task)
                            ? alpha(theme.palette.success.main, 0.08)
                            : theme.palette.background.paper,
                        '&:active': { transform: 'scale(0.995)' }
                    }}
                    onClick={() => props.onView(task)}
                >
                    <Stack spacing={1}>
                        <Stack spacing={0.25}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                                {`#${task.cardNumber} - ${task.title}`}
                            </Typography>
                            {task.subTitle && (
                                <Typography variant="body2" color="text.secondary">
                                    {`${t(task.category)}`} <br />
                                    {task.subTitle} <br />
                                    {`${formatFrequency(task)}`}
                                </Typography>
                            )}
                        </Stack>

                        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between">
                            <Stack spacing={0.5}>
                                {formatMonths(task)}
                            </Stack>

                            <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                                <IconButton size="small" onClick={() => props.onAssignments(task)}>
                                    <AssignmentIndIcon fontSize="small" color="action" />
                                </IconButton>
                                {props.canManage && (
                                    <>
                                        <IconButton size="small" onClick={() => props.onEdit(task)}>
                                            <EditIcon fontSize="small" color="primary" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => props.onDelete(task)}>
                                            <DeleteIcon fontSize="small" color="error" />
                                        </IconButton>
                                    </>
                                )}
                            </Stack>
                        </Stack>
                    </Stack>
                </Paper>
            ))}

            {props.tasks.length === 0 && (
                <Paper elevation={0} sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{t("no_data")}</Typography>
                </Paper>
            )}
        </Stack>
    );
};

export default HallMaintenanceTasksMobileList;
